import { formatMoney, formatNumber, KpiTile, type Column } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { ResourceTable } from '../ResourceTable.tsx';
import { carrierNameOf, useCarriers } from '../settings/carriers.ts';
import { usePageContext } from '../usePageContext.ts';
import { useFinanceOverview, type CarrierFigures } from './finance.ts';
import { PeriodChips } from '../PeriodChips.tsx';
import { reportPeriodOf, type PeriodPreset } from '../report-period.ts';
import styles from './Finance.module.css';
import { FinanceFrame } from './FinanceFrame.tsx';
import type { FinanceSection } from './FinanceSections.tsx';

export type FinanceOverviewPageProps = {
  api: TypedClient;
  shipping: ShippingClient;
  onSection: (section: FinanceSection) => void;
};

type Amount = { readonly count: number; readonly amount: string };

export const FinanceOverviewPage = ({ api, shipping, onSection }: FinanceOverviewPageProps) => {
  const { t, locale } = usePageContext();
  const [preset, setPreset] = useState<PeriodPreset>('this_month');
  const [openedAt] = useState(() => new Date());
  const overview = useFinanceOverview(api, reportPeriodOf(preset, openedAt));
  const carriers = useCarriers(shipping);
  const currency = overview.data?.currency;
  const money = (amount: string) =>
    currency === undefined ? '—' : formatMoney({ amount, currency }, locale);
  const withCount = (figure: Amount) => `${money(figure.amount)} · ${formatNumber(figure.count, locale)}`;
  const carrierName = (code: string): string => carrierNameOf(carriers.data?.items, code);
  const totals = overview.data?.totals;

  const columns: readonly Column<CarrierFigures>[] = [
    {
      id: 'carrier',
      header: t('finance.carrier'),
      primary: true,
      cell: (row) => carrierName(row.carrierCode),
    },
    {
      id: 'delivered',
      header: t('finance.delivered'),
      numeric: true,
      cell: (row) => withCount(row.delivered),
    },
    { id: 'remitted', header: t('finance.remitted'), numeric: true, cell: (row) => withCount(row.remitted) },
    { id: 'fees', header: t('finance.carrierFees'), numeric: true, cell: (row) => money(row.carrierFees) },
    {
      id: 'returnFees',
      header: t('finance.returnFees'),
      numeric: true,
      cell: (row) => money(row.returnFees.amount),
    },
    { id: 'awaiting', header: t('finance.awaiting'), numeric: true, cell: (row) => withCount(row.awaiting) },
    { id: 'disputes', header: t('finance.disputes'), numeric: true, cell: (row) => withCount(row.disputes) },
  ];

  return (
    <FinanceFrame section="overview" context={t('finance.context')} onSection={onSection}>
      <div className={styles['toolbar']}>
        <PeriodChips value={preset} onChange={setPreset} />
      </div>
      <div className={styles['kpis']}>
        <KpiTile
          label={t('finance.kpiAwaiting')}
          value={totals === undefined ? undefined : money(totals.awaiting.amount)}
          loading={overview.isPending}
        />
        <KpiTile
          label={t('finance.kpiRemitted')}
          value={totals === undefined ? undefined : money(totals.remitted.amount)}
          loading={overview.isPending}
        />
        <KpiTile
          label={t('finance.kpiFees')}
          value={totals === undefined ? undefined : money(totals.carrierFees)}
          loading={overview.isPending}
        />
        <KpiTile
          label={t('finance.kpiExpenses')}
          value={overview.data === undefined ? undefined : money(overview.data.expenses)}
          loading={overview.isPending}
        />
        <KpiTile
          label={t('finance.kpiNet')}
          value={overview.data === undefined ? undefined : money(overview.data.net)}
          loading={overview.isPending}
        />
        <KpiTile
          label={t('finance.kpiDisputes')}
          value={totals === undefined ? undefined : formatNumber(totals.disputes.count, locale)}
          loading={overview.isPending}
        />
      </div>
      <ResourceTable
        query={overview}
        columns={columns}
        rows={overview.data?.carriers ?? []}
        rowKey={(row) => row.carrierCode}
        emptyTitle={t('finance.noFigures')}
        emptyDescription={t('finance.noFiguresHelp')}
      />
    </FinanceFrame>
  );
};
